import {
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import { useHealthToolsCache } from '@hhgtech/hhg-components'
import {
  LOCALE_HAS_COMMUNITY_SITE,
  QUERY_HEALTH_TOOL,
} from '@hhgtech/hhg-components/constants'
import { DatePicker, Text } from '@hhgtech/hhg-components/mantine'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { DueDateInfoType } from 'components/pages/healthTools/due-date'
import { useAppContext } from 'hooks/useAppContext'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { API_DATE_FORMAT } from 'interfaces/constants'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'

import { FormControl, StyledLastPeriodFormContainer } from './index.styled'
import { DueDateCalculatorType, getDueDateIUI, useDueDateStore } from './utils'

type Props = {
  onSubmit?: (info: DueDateInfoType, redirectLink?: string) => void
  isEmbed?: boolean
  controlledSubmit?: boolean
}

export type HandlerProps = {
  submitForm: any
}

const DueDateFormIUI: ForwardRefRenderFunction<HandlerProps, Props> = (
  { onSubmit, isEmbed = false, controlledSubmit },
  ref,
) => {
  const { formatMessage: f } = useIntl()
  const {
    state: { isLoggedIn },
  } = useAppContext()
  const { locale, router } = useRouterExtend()
  const { getHealthToolLink } = useHealthToolsCache(locale)
  const { setIsCalculating } = useDueDateStore()
  const linkTool = useMemo(() => getHealthToolLink(HEALTH_TOOL_TYPE.DUE_DATE), [
    getHealthToolLink,
  ])

  const [dateOfIUIProcedure, setDateOfIUIProcedure] = useState(new Date())

  useImperativeHandle(ref, () => ({
    submitForm: onSubmitForm,
  }))

  const onSubmitForm = async () => {
    const currentUrl = isEmbed
      ? `${window.location.origin}/${linkTool}`
      : window.location.origin + window.location.pathname

    const params = [
      `${QUERY_HEALTH_TOOL.type}=${DueDateCalculatorType.IUI}`,
      `${QUERY_HEALTH_TOOL.startDate}=${dayjs(dateOfIUIProcedure).format(
        API_DATE_FORMAT,
      )}`,
      `${QUERY_HEALTH_TOOL.additional}= `,
      LOCALE_HAS_COMMUNITY_SITE.includes(locale) ? 'joincommunity=1' : '',
    ]
      .filter(Boolean)
      .join('&')
    const redirectLink = currentUrl + `?${params}`

    if (controlledSubmit) {
      setIsCalculating(true)
      const info = await getDueDateIUI(dateOfIUIProcedure)
      onSubmit && onSubmit(info, redirectLink)

      return
    }

    if (!isLoggedIn) {
      redirectToSSOWithReturnUrl(redirectLink, 'due-date')
    }
    if (isEmbed) {
      router.push(redirectLink)
    } else {
      setIsCalculating(true)
      const info = await getDueDateIUI(dateOfIUIProcedure)
      onSubmit && onSubmit(info, redirectLink)
    }
  }

  return (
    <StyledLastPeriodFormContainer>
      <FormControl>
        <DatePicker
          label={
            <Text as="label" size="s4">
              {f({
                id: 'dueDatePage.date_of_IUI_procedure',
                defaultMessage: 'Date of IUI procedure',
              })}
            </Text>
          }
          value={dateOfIUIProcedure}
          onChange={(value) => {
            setDateOfIUIProcedure(value || new Date())
          }}
          excludeDate={(date) =>
            dayjs(date).isBefore(dayjs().subtract(43, 'week'))
          }
          size="md"
        />
      </FormControl>
    </StyledLastPeriodFormContainer>
  )
}

export default forwardRef(DueDateFormIUI)
